import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Title,
  Text,
  TextInput,
  Button,
  Stack,
  Alert,
  Flex,
  Image,
  Anchor,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { AlertCircle, Mail, Check, ArrowLeft } from 'react-feather';
import { authService } from '../../services/api/auth';
import classes from './LoginPage.module.css';
import medleyLogoWhite from '../../assets/medley-logo-white.png';

interface ForgotPasswordFormValues {
  email: string;
}

export function ForgotPasswordPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<ForgotPasswordFormValues>({
    initialValues: {
      email: '',
    },
    validate: {
      email: value =>
        /^\S+@\S+$/.test(value) ? null : 'Invalid email address',
    },
  });

  const handleSubmit = async (values: ForgotPasswordFormValues) => {
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await authService.forgotPassword(values.email);
      setIsSuccess(true);
    } catch {
      // Always show success message to prevent email enumeration
      setIsSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <Box className={classes.wrapper}>
        <div className={classes.pattern} />
        <Flex className={classes.container} justify="center">
          <Paper className={classes.formCard} p={40} radius="lg" shadow="xl" maw={420}>
            <Stack align="center" gap="md">
              <Box
                p="md"
                style={{
                  backgroundColor: 'var(--mantine-color-green-0)',
                  borderRadius: '50%',
                }}
              >
                <Check size={32} color="var(--mantine-color-green-6)" />
              </Box>
              <Title order={3} ta="center">
                Check your email
              </Title>
              <Text c="dimmed" ta="center">
                If an account exists with that email address, we've sent instructions
                to reset your password.
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                Didn't receive the email? Check your spam folder or try again.
              </Text>
              <Stack gap="xs" w="100%" mt="md">
                <Button
                  variant="light"
                  color="medleyGreen"
                  onClick={() => {
                    setIsSuccess(false);
                    form.reset();
                  }}
                  fullWidth
                >
                  Try another email
                </Button>
                <Button
                  variant="subtle"
                  color="gray"
                  component={Link}
                  to="/login"
                  fullWidth
                  leftSection={<ArrowLeft size={16} />}
                >
                  Back to login
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Flex>
      </Box>
    );
  }

  return (
    <Box className={classes.wrapper}>
      <div className={classes.pattern} />

      <Flex className={classes.container}>
        {/* Left Side - Branding */}
        <Box className={classes.branding} visibleFrom="md">
          <Box
            mb={48}
            p="sm"
            style={{
              backgroundColor: '#2D4739',
              borderRadius: 8,
              display: 'inline-block',
            }}
          >
            <Image src={medleyLogoWhite} alt="Medley" h={28} w="auto" fit="contain" />
          </Box>

          <Title order={1} className={classes.headline}>
            Forgot your
            <br />
            password?
          </Title>

          <Text size="lg" c="dimmed" mt="lg" maw={400}>
            No worries! Enter your email address and we'll send you a link to
            reset your password.
          </Text>
        </Box>

        {/* Right Side - Form */}
        <Box className={classes.formSection}>
          <Paper className={classes.formCard} p={40} radius="lg" shadow="xl">
            <Title order={2} mb={4}>
              Reset password
            </Title>

            <Text c="dimmed" size="sm" mb="xl">
              Enter your email to receive a password reset link
            </Text>

            {submitError && (
              <Alert
                icon={<AlertCircle size={16} />}
                title="Error"
                color="red"
                mb="md"
                onClose={() => setSubmitError(null)}
                withCloseButton
              >
                {submitError}
              </Alert>
            )}

            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="md">
                <TextInput
                  label="Email address"
                  placeholder="you@company.com"
                  leftSection={<Mail size={18} />}
                  size="md"
                  {...form.getInputProps('email')}
                />

                <Button
                  type="submit"
                  fullWidth
                  size="md"
                  mt="sm"
                  loading={isSubmitting}
                >
                  Send reset link
                </Button>

                <Anchor
                  component={Link}
                  to="/login"
                  size="sm"
                  c="dimmed"
                  ta="center"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                >
                  <ArrowLeft size={14} />
                  Back to login
                </Anchor>
              </Stack>
            </form>
          </Paper>
        </Box>
      </Flex>
    </Box>
  );
}

export default ForgotPasswordPage;
